import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import LessonFinancialFields from './LessonFinancialFields';

const MobileTraining = ({ reservation, onClose }) => {
  const { currentUser, aircraft, people, currentOrg, settings } = useApp();
  const [lessonData, setLessonData] = useState({ 
    simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', 
    purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '', referralCredit: ''
  });
  const [lessonMessage, setLessonMessage] = useState('');
  const [lessonId, setLessonId] = useState(null);
  const [lessonFeeOverride, setLessonFeeOverride] = useState(null);
  const [aircraftData, setAircraftData] = useState(null);
  const [loading, setLoading] = useState(false);
  const instructionOwnerAircraftFee = (() => {
    const rawSettings = Array.isArray(settings) ? settings[0] : settings;
    const parsedFee = Number(rawSettings?.instructionOwnerAircraftFee);
    return Number.isFinite(parsedFee) ? parsedFee : 10;
  })();

  const fetchLessonData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/lesson/reservation/${reservation.id}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      
      if (response.ok) {
        const result = await response.json();
        const data = Array.isArray(result) ? result.find(item => Number(item.userId) === Number(reservation.userIdClient)) || result[0] || {} : result;
        setLessonFeeOverride(data.fee != null ? String(data.fee) : null);
        setLessonData({
          simulator: data.simulator || '',
          dualReceived: data.dualReceived || '',
          ground: data.ground || '',
          debrief: data.debrief || '',
          nextFlight: data.nextFlight || '',
          purchases: data.purchases ?? '',
          credit: data.credit ?? '',
          flightTime: data.flightTime || '',
          payment: data.payment ?? '',
          paymentType: data.paymentType || '',
          fee: data.fee ?? '',
          referralCredit: data.referralCredit ?? ''
        });
        setLessonId(data.id || null);
      } else {
        setLessonFeeOverride(null);
        setLessonData({ 
          simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', 
          purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '', referralCredit: ''
        });
        setLessonId(null);
      }
    } catch (error) {
      console.error('Error fetching lesson data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAircraftData = async () => {
    if (!reservation.aircraftId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log/reservation/${reservation.id}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAircraftData(data);
      }
    } catch (error) {
      console.error('Error fetching aircraft data:', error);
    }
  };

  const handleSaveLesson = async () => {
    if (!canEditFinancials) return;
    const client = people.find(p => p.id === reservation.userIdClient);
    const reservationAircraft = aircraft.find(a => a.id === reservation.aircraftId);
    const reservationDate = reservation.startDate ? reservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    
    const flightTime = lessonData.flightTime ? parseFloat(lessonData.flightTime) : 
      (aircraftData && aircraftData.hobbsEnd ? 
        parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart || 0) : null);
    
    let instructorRate = 0;
    if (reservation.userIdInstructor) {
      try {
        const instructorResponse = await fetch(`${API_BASE_URL}/user/${reservation.userIdInstructor}`, {
          headers: { 'session-id': currentUser?.sessionId }
        });
        if (instructorResponse.ok) {
          const instructorData = await instructorResponse.json();
          instructorRate = instructorData.rate || 0;
        }
      } catch (error) {
        console.error('Error fetching instructor rate:', error);
      }
    }
    const payload = {
      userId: reservation.userIdClient,
      reservationId: reservation.id,
      date: reservationDate,
      aircraftId: reservation.aircraftId || null,
      instructorId: reservation.userIdInstructor,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      flightTime: lessonData.flightTime ? parseFloat(lessonData.flightTime) : flightTime,
      simulator: lessonData.simulator ? parseFloat(lessonData.simulator) : null,
      dualReceived: lessonData.dualReceived ? parseFloat(lessonData.dualReceived) : null,
      ground: lessonData.ground ? parseFloat(lessonData.ground) : null,
      debrief: lessonData.debrief,
      nextFlight: lessonData.nextFlight,
      purchases: lessonData.purchases ? parseFloat(lessonData.purchases) : null,
      referralCredit: lessonData.referralCredit !== '' ? parseFloat(lessonData.referralCredit) : null,
      credit: lessonData.credit ? parseFloat(lessonData.credit) : null,
      payment: lessonData.payment ? parseFloat(lessonData.payment) : null,
      paymentType: lessonData.paymentType || null,
      fee: parseFloat(lessonFeeOverride ?? computeLessonTotalFrom(lessonData)) || 0
    };
    
    if (lessonId) {
      payload.id = lessonId;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (!lessonId && result.id) {
          setLessonId(result.id);
        }
        setLessonMessage('Lesson data saved successfully');
        setTimeout(() => setLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setLessonMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setLessonMessage(`Error: ${error.message}`);
    }
  };

  const calculateLessonTotal = () => {
    const instructorId = Number(reservation?.userIdInstructor);
    const instructor = people.find((person) => Number(person.id) === instructorId);
    const instructorRate = instructor?.rate || 0;
    const dualHours = lessonData.dualReceived ? parseFloat(lessonData.dualReceived) : 0;
    const dualRate = instructorRate + instructionOwnerAircraftFee;
    const groundHours = lessonData.ground ? parseFloat(lessonData.ground) : 0;
    const purchases = lessonData.purchases ? parseFloat(lessonData.purchases) : 0;
    const total = (dualHours * dualRate) + (groundHours * instructorRate) + purchases - (parseFloat(lessonData.referralCredit) || 0);
    return total.toFixed(2);
  };

  const computeLessonTotalFrom = (ldata) => {
    const instructorId = Number(reservation?.userIdInstructor);
    const instructor = people.find((person) => Number(person.id) === instructorId);
    const instructorRate = instructor?.rate || 0;
    const dualHours = ldata.dualReceived ? parseFloat(ldata.dualReceived) : 0;
    const dualRate = instructorRate + instructionOwnerAircraftFee;
    const groundHours = ldata.ground ? parseFloat(ldata.ground) : 0;
    const purchases = ldata.purchases ? parseFloat(ldata.purchases) : 0;
    const total = (dualHours * dualRate) + (groundHours * instructorRate) + purchases - (parseFloat(ldata.referralCredit) || 0);
    return total.toFixed(2);
  };

  useEffect(() => {
    fetchLessonData();
    fetchAircraftData();
  }, [reservation.id]);

  // live recalc when relevant fields change
  useEffect(() => {
    if (lessonFeeOverride !== null) return;
    try {
      const fee = calculateLessonTotal();
      if ((lessonData.fee || '') !== fee) {
        setLessonData(prev => ({ ...prev, fee }));
      }
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonData.simulator, lessonData.flightTime, lessonData.dualReceived, lessonData.ground, lessonData.purchases, lessonData.referralCredit, settings?.simRate, settings?.instructionOwnerAircraftFee, aircraftData, currentOrg, lessonFeeOverride]);

  const canEdit = (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser' || 
                   currentUser?.user?.orgRole === 'staff' || reservation.userIdInstructor === currentUser?.user?.id);
  const canEditFinancials = !(reservation.safetyPilotId && Number(reservation.safetyPilotId) === Number(currentUser?.user?.id))
    && (currentUser?.user?.orgRole !== 'client' || Number(reservation.userIdClient) === Number(currentUser?.user?.id));

  return (
    <div style={{ height: '100vh', backgroundColor: 'white', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '15px', backgroundColor: '#28a745', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>{reservation.userIdInstructor ? 'Lesson Details' : 'Flight Log'}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>×</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '15px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading...</div>
        ) : (
          <div>
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Simulator:</label>
              <input 
                type="number" 
                step="0.1" 
                value={lessonData.simulator} 
                onChange={(e) => { const v = e.target.value; const fee = computeLessonTotalFrom({...lessonData, simulator: v}); setLessonData({...lessonData, simulator: v, fee}); }} 
                onBlur={() => { const fee = calculateLessonTotal(); setLessonData({...lessonData, fee}); }}
                style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: canEdit ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit} 
              />
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Flight Time:</label>
              <input 
                type="number" 
                step="0.1" 
                value={lessonData.flightTime || (aircraftData && aircraftData.hobbsEnd && aircraftData.hobbsStart ? (parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)).toFixed(1) : '')} 
                onChange={(e) => setLessonData({...lessonData, flightTime: e.target.value})} 
                onBlur={() => setLessonData({...lessonData, fee: calculateLessonTotal()})} 
                style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: canEdit ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit} 
              />
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Dual Given:</label>
              <input 
                type="number" 
                step="0.1" 
                value={lessonData.dualReceived} 
                onChange={(e) => setLessonData({...lessonData, dualReceived: e.target.value})} 
                onBlur={() => setLessonData({...lessonData, fee: calculateLessonTotal()})} 
                style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: (canEdit && reservation.userIdInstructor) ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit || !reservation.userIdInstructor} 
              />
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Ground:</label>
              <input 
                type="number" 
                step="0.1" 
                value={lessonData.ground} 
                onChange={(e) => setLessonData({...lessonData, ground: e.target.value})} 
                onBlur={() => setLessonData({...lessonData, fee: calculateLessonTotal()})} 
                style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: (canEdit && reservation.userIdInstructor) ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit || !reservation.userIdInstructor} 
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Debrief:</label>
              <textarea 
                value={lessonData.debrief} 
                onChange={(e) => setLessonData({...lessonData, debrief: e.target.value})} 
                rows="3" 
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical', backgroundColor: canEdit ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit} 
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Next Flight:</label>
              <textarea 
                value={lessonData.nextFlight} 
                onChange={(e) => setLessonData({...lessonData, nextFlight: e.target.value})} 
                rows="3" 
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical', backgroundColor: canEdit ? 'white' : '#f5f5f5' }} 
                readOnly={!canEdit} 
              />
            </div>

            <LessonFinancialFields value={lessonData} onChange={setLessonData} total={lessonFeeOverride ?? computeLessonTotalFrom(lessonData)} onTotalChange={setLessonFeeOverride} disabled={!canEditFinancials} />

            {lessonMessage && (
              <div style={{ 
                padding: '10px', 
                marginBottom: '15px', 
                borderRadius: '4px', 
                backgroundColor: lessonMessage.includes('Error') ? '#f8d7da' : '#d4edda', 
                color: lessonMessage.includes('Error') ? '#721c24' : '#155724', 
                border: `1px solid ${lessonMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` 
              }}>
                {lessonMessage}
              </div>
            )}

            {canEditFinancials && (
              <div style={{ textAlign: 'center', marginTop: '66px', paddingBottom: '20px' }}>
                <button 
                  onClick={handleSaveLesson} 
                  style={{ 
                    width: '100%',
                    padding: '12px', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer' 
                  }}
                >
                  Save Lesson
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileTraining;